import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import { SystemUserAccountService, UserAccount, SaveUserAccountRequest } from '../../../services/system-user-account.service';
import { GridColumn } from '../../../shared/smart-grid/smart-grid.models';

@Component({
  selector: 'app-user-account-management',
  templateUrl: './user-account-management.component.html',
  styleUrls: ['./user-account-management.component.scss']
})
export class UserAccountManagementComponent implements OnInit {
  // Data
  userAccounts: any = { rows: [], totalRows: 0 };
  
  // Form
  userAccountForm: FormGroup;
  currentUserAccount: UserAccount | null = null;
  isEditing = false;
  
  // Modals
  private modalRef?: NgbModalRef;
  @ViewChild('userAccountModal') userAccountModal!: TemplateRef<any>;
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  
  // Delete
  userAccountToDelete: UserAccount | null = null;
  deleteDomainCount: number = 0;
  
  // Grid columns
  columns: GridColumn<any>[] = [];

  constructor(
    private systemUserAccountService: SystemUserAccountService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal
  ) {
    this.userAccountForm = this.formBuilder.group({
      userAccountID: [0],
      fullName: ['', [Validators.required, Validators.maxLength(100)]],
      address: ['', Validators.maxLength(50)],
      address2: ['', Validators.maxLength(50)],
      zipCode: ['', Validators.maxLength(6)],
      city: ['', Validators.maxLength(30)],
      email: ['', [Validators.maxLength(100), Validators.email]],
      phone: ['', Validators.maxLength(12)]
    });
  }

  ngOnInit() {
    this.buildColumns();
    this.loadUserAccounts();
  }

  get f() {
    return this.userAccountForm.controls;
  }

  /**
   * Build grid columns
   */
  buildColumns() {
    this.columns = [
      {
        field: 'userAccountID',
        title: this.translateService.instant('SystemAdmin.userAccountID'),
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'number'
      },
      {
        field: 'fullName',
        title: this.translateService.instant('SystemAdmin.fullName'),
        width: 250,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'email',
        title: this.translateService.instant('SystemAdmin.email'),
        width: 250,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'phone',
        title: this.translateService.instant('SystemAdmin.phone'),
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'city',
        title: this.translateService.instant('SystemAdmin.city'),
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'domainCount',
        title: this.translateService.instant('SystemAdmin.domainCount'),
        width: 120,
        sortable: true,
        filterable: false
      },
      {
        field: 'actions',
        title: this.translateService.instant('General.actions'),
        width: 150,
        sortable: false,
        filterable: false
      }
    ];
  }

  /**
   * Load all user accounts
   */
  loadUserAccounts() {
    this.systemUserAccountService.getAllUserAccounts().subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          this.userAccounts = {
            rows: response.result,
            totalRows: response.result.length
          };
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant('SystemAdmin.errorLoadingUserAccounts'));
        }
      },
      error: (error) => {
        console.error('Error loading user accounts:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingUserAccounts')]
        });
      }
    });
  }

  /**
   * Open modal to create new user account
   */
  openCreateModal() {
    this.isEditing = false;
    this.currentUserAccount = null;
    this.userAccountForm.reset({ userAccountID: 0 });
    this.modalRef = this.modalService.open(this.userAccountModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Open modal to edit user account
   */
  openEditModal(userAccount: UserAccount) {
    this.isEditing = true;
    this.currentUserAccount = userAccount;
    this.userAccountForm.patchValue({
      userAccountID: userAccount.userAccountID,
      fullName: userAccount.fullName,
      address: userAccount.address || '',
      address2: userAccount.address2 || '',
      zipCode: userAccount.zipCode || '',
      city: userAccount.city || '',
      email: userAccount.email || '',
      phone: userAccount.phone || ''
    });
    this.modalRef = this.modalService.open(this.userAccountModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Save user account (create or update)
   */
  saveUserAccount() {
    if (this.userAccountForm.invalid) {
      Object.keys(this.userAccountForm.controls).forEach(key => {
        this.userAccountForm.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.userAccountForm.value;
    const request: SaveUserAccountRequest = {
      userAccountID: this.isEditing ? formValue.userAccountID : undefined,
      fullName: formValue.fullName,
      address: formValue.address || undefined,
      address2: formValue.address2 || undefined,
      zipCode: formValue.zipCode || undefined,
      city: formValue.city || undefined,
      email: formValue.email || undefined,
      phone: formValue.phone || undefined
    };

    const operation = this.isEditing
      ? this.systemUserAccountService.updateUserAccount(formValue.userAccountID, request)
      : this.systemUserAccountService.createUserAccount(request);

    operation.subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant(
              this.isEditing ? 'SystemAdmin.userAccountUpdated' : 'SystemAdmin.userAccountCreated'
            )]
          });
          this.closeModal();
          this.loadUserAccounts();
        } else {
          const errorMessage = response?.errorMessage?.[0]?.message || 
            this.translateService.instant('General.operationFailed');
          this.toastService.show({ type: 'error', data: [errorMessage] });
        }
      },
      error: (error) => {
        console.error('Error saving user account:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('General.operationFailed')]
        });
      }
    });
  }

  /**
   * Open delete confirmation modal
   */
  openDeleteModal(userAccount: UserAccount) {
    this.userAccountToDelete = userAccount;
    
    // Check domain count before allowing delete
    this.systemUserAccountService.getDomainCount(userAccount.userAccountID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.deleteDomainCount = response.result || 0;
          this.modalRef = this.modalService.open(this.deleteConfirmModal, { backdrop: 'static' });
        }
      },
      error: (error) => {
        console.error('Error checking domain count:', error);
        this.deleteDomainCount = 0;
        this.modalRef = this.modalService.open(this.deleteConfirmModal, { backdrop: 'static' });
      }
    });
  }

  /**
   * Confirm delete user account
   */
  confirmDelete() {
    if (!this.userAccountToDelete) return;

    this.systemUserAccountService.deleteUserAccount(this.userAccountToDelete.userAccountID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.userAccountDeleted')]
          });
          this.closeModal();
          this.loadUserAccounts();
        } else {
          const errorMessage = response?.errorMessage?.[0]?.message || 
            this.translateService.instant('General.operationFailed');
          this.toastService.show({ type: 'error', data: [errorMessage] });
        }
      },
      error: (error) => {
        console.error('Error deleting user account:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('General.operationFailed')]
        });
      }
    });
  }

  /**
   * Close modal
   */
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = undefined;
    }
    this.userAccountForm.reset();
    this.currentUserAccount = null;
    this.userAccountToDelete = null;
  }
}
